import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { useTheme } from "../context/ThemeContext";
import { useToast } from "../hooks/use-toast";
import {
  Save, Sun, Moon, Monitor, MailX, MailCheck, Plus, Trash2, ShieldCheck,
  Palette, Newspaper, LayoutGrid, Radio, Megaphone, Wrench, Users2, SlidersHorizontal, Flame, Link2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import EscalationEditor from "../components/EscalationEditor";
import VigilNewsEditor from "../components/VigilNewsEditor";
import UsersManager from "../components/UsersManager";
import NewsFeedsEditor from "../components/NewsFeedsEditor";
import AppsManager from "../components/AppsManager";
import AnnouncementsEditor from "../components/AnnouncementsEditor";
import CollapsibleSection from "../components/CollapsibleSection";
import WatchlistEditor from "../components/WatchlistEditor";
import ConnectedAccounts from "../components/ConnectedAccounts";
import MfaSettings from "../components/MfaSettings";
import MfaAdminSettings from "../components/MfaAdminSettings";
import { cn } from "../lib/utils";

const NEWS_SOURCES = [
  { key: "cisa", label: "CISA", region: "US" },
  { key: "msrc", label: "Microsoft MSRC", region: "US" },
  { key: "nvd",  label: "NIST NVD", region: "US" },
  { key: "cccs", label: "CCCS (Canada)", region: "CA" },
];

// Backward-compat: old sub-tab URLs redirect to their new home so existing links
// (masquerade return, NewsPanel edit, AlertBanner) keep working.
const TAB_ALIASES = {
  marketing:    "branding",
  news:         "branding",
  apps:         "engineering",
  "super-admin": "admin",
  platform:     "branding",
};

export default function SettingsPage() {
  const { toast } = useToast();
  const { mode, setMode } = useTheme();
  const { user } = useAuth();
  const { refresh: refreshGlobalSettings } = useSettings();
  const { tab: tabParam } = useParams();
  const navigate = useNavigate();
  const isAdmin = user?.role === "Super Admin" || (Array.isArray(user?.scopes) && user.scopes.includes("administrator"));

  const TABS = [
    { key: "account",     label: "Account",      icon: Link2,
      hint: "Connected sign-in accounts (Google, Microsoft)" },
    { key: "branding",    label: "Branding",     icon: Palette,
      hint: "Brand identity, appearance, announcements & news feeds" },
    { key: "engineering", label: "Engineering",  icon: Wrench,
      hint: "Apps, dashboard layout, on-call rotation, email safety",
      requiresAdmin: false },
    ...(isAdmin ? [{ key: "admin", label: "Admin", icon: Users2,
      hint: "Users, scopes, masquerade, industry news sources" }] : []),
  ];
  const resolved = TAB_ALIASES[tabParam] || tabParam;
  const tab = TABS.find((t) => t.key === resolved)?.key || "branding";

  const [settings, setSettings] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // Normalise old URLs into the new tab shell without breaking history.
  useEffect(() => {
    if (tabParam && TAB_ALIASES[tabParam]) {
      navigate(`/settings/${TAB_ALIASES[tabParam]}`, { replace: true });
    }
  }, [tabParam, navigate]);

  const patch = (p) => setSettings((s) => ({ ...s, ...p }));

  // Persist a partial patch immediately (used by critical toggles like email pause)
  const patchAndSave = async (p) => {
    if (!settings) return;
    const next = { ...settings, ...p };
    setSettings(next);
    try {
      const { data } = await api.put("/settings", next);
      setSettings(data);
      refreshGlobalSettings();
    } catch (e) {
      toast({ title: "Update failed", variant: "destructive" });
    }
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { ...settings, theme: mode });
      setSettings(data);
      toast({ title: "Settings saved" });
    } catch (e) {
      toast({ title: "Save failed", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const addAllowAndSave = async () => {
    const v = newEmail.trim().toLowerCase();
    if (!v || !settings) return;
    if ((settings.qa_allowlist || []).includes(v)) return;
    await patchAndSave({ qa_allowlist: [...(settings.qa_allowlist || []), v] });
    setNewEmail("");
  };
  const removeAllowAndSave = (v) => patchAndSave({ qa_allowlist: (settings.qa_allowlist || []).filter((x) => x !== v) });

  const toggleSource = (k) => {
    const cur = settings?.news_sources || [];
    patch({ news_sources: cur.includes(k) ? cur.filter((s) => s !== k) : [...cur, k] });
  };

  if (!settings) return <div className="h-40 rounded-xl bg-muted/40 animate-pulse" />;

  const activeTabMeta = TABS.find((t) => t.key === tab);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[12px] text-muted-foreground inline-flex items-center gap-1">
            <span className="cursor-pointer hover:text-foreground" onClick={() => navigate("/")}>Dashboard</span>
            <span>›</span>
            <span className="text-foreground font-medium">Settings</span>
            <span>›</span>
            <span className="text-foreground font-medium">{activeTabMeta?.label}</span>
          </div>
          <h1 className="text-[26px] font-semibold tracking-tight mt-1">Settings</h1>
          <p className="text-[13px] text-muted-foreground">{activeTabMeta?.hint}</p>
        </div>
        {isAdmin && (
          <Button onClick={save} disabled={saving} className="gap-2" data-testid="settings-save-btn"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}</Button>
        )}
      </div>
      {!isAdmin && (
        <div className="rounded-lg border border-border bg-muted/30 text-muted-foreground p-3 text-[12.5px] flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> Most settings here are managed by Super Admins — you&apos;re viewing in read-only mode.
        </div>
      )}

      {/* Sub-tab pills */}
      <div className="flex items-center gap-2 border-b border-border pb-3 overflow-x-auto tabler-scroll">
        {TABS.map((t) => {
          const Active = t.icon;
          const isActive = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`settings-tab-${t.key}`}
              onClick={() => navigate(`/settings/${t.key}`)}
              className={cn(
                "inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[13px] font-medium whitespace-nowrap transition-colors border",
                isActive
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-muted-foreground border-border hover:text-foreground hover:border-primary/40"
              )}
            >
              <Active className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ---------- ACCOUNT ---------- */}
      {tab === "account" && (
        <div className="space-y-4">
          <CollapsibleSection id="connected-accounts" title="Connected accounts" description="Sign in with Google or Microsoft, or switch between them any time." icon={Link2} defaultOpen>
            <ConnectedAccounts />
          </CollapsibleSection>
          <CollapsibleSection id="mfa-settings" title="Two-factor authentication" description="Add a passkey or authenticator app for extra login security. SMS is not offered — it's less secure." icon={ShieldCheck} defaultOpen>
            <MfaSettings />
          </CollapsibleSection>
        </div>
      )}

      {/* ---------- BRANDING ---------- */}
      {tab === "branding" && (
        <div className="space-y-4">
          <CollapsibleSection id="brand-identity" title="Brand identity" description="Name, subtitle, tagline & support email shown across every page." icon={Palette}>
            {isAdmin ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <Label>Brand name</Label>
                  <Input value={settings.brand_name || ""} onChange={(e) => patch({ brand_name: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Subtitle</Label>
                  <Input value={settings.subtitle || ""} onChange={(e) => patch({ subtitle: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Tagline</Label>
                  <Input value={settings.tagline || ""} onChange={(e) => patch({ tagline: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Support email</Label>
                  <Input value={settings.support_email || ""} onChange={(e) => patch({ support_email: e.target.value })} className="mt-1" />
                </div>
              </div>
            ) : (
              <div className="text-[13px] text-muted-foreground">Brand identity is managed by Super Admins.</div>
            )}
          </CollapsibleSection>

          {isAdmin && (
            <CollapsibleSection id="announcements" title="Featured announcements" description="Schedule the hero banner shown on the Dashboard." icon={Megaphone}>
              <AnnouncementsEditor embedded />
            </CollapsibleSection>
          )}

          <CollapsibleSection id="escalation" title="Escalation — Partners & Staff" description="Renders as the 3-column contact table on the Dashboard." icon={Wrench}>
            {isAdmin ? (
              <EscalationEditor embedded />
            ) : (
              <div className="text-[13px] text-muted-foreground">Escalation contacts are managed by Super Admins.</div>
            )}
          </CollapsibleSection>

          {isAdmin ? (
            <CollapsibleSection id="vigil-news" title="Vigil News" description="Internal announcements shown on the /news page." icon={Newspaper}>
              <VigilNewsEditor embedded />
            </CollapsibleSection>
          ) : (
            <CollapsibleSection id="vigil-news-readonly" title="Vigil News" description="Internal announcements are managed by Super Admins." icon={Newspaper}>
              <div className="text-[13px] text-muted-foreground">You don&apos;t have permission to edit Vigil News.</div>
            </CollapsibleSection>
          )}

          <CollapsibleSection id="appearance" title="Appearance" description="Choose light, dark or follow the system." icon={Palette}>
            <RadioGroup value={mode} onValueChange={setMode} className="grid grid-cols-3 gap-2 max-w-md">
              {[
                { v: "light", label: "Light", Icon: Sun },
                { v: "dark", label: "Dark", Icon: Moon },
                { v: "system", label: "System", Icon: Monitor },
              ].map(({ v, label, Icon }) => (
                <Label key={v} htmlFor={`t-${v}`} className={`cursor-pointer rounded-xl border p-3 flex flex-col items-center gap-2 ${mode === v ? "border-primary bg-primary/5" : "border-border"}`}>
                  <RadioGroupItem value={v} id={`t-${v}`} className="sr-only" />
                  <Icon className="w-5 h-5" />
                  <span className="text-[13px] font-medium">{label}</span>
                </Label>
              ))}
            </RadioGroup>
          </CollapsibleSection>

          {isAdmin && (
            <CollapsibleSection id="platform-identity" title="Platform identity" description="Primary colour + version label used in the header." icon={SlidersHorizontal}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <Label>Primary colour</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <input type="color" value={settings.primary_color || "#49C22E"} onChange={(e) => patch({ primary_color: e.target.value })} className="h-9 w-14 rounded border border-border cursor-pointer" />
                    <Input value={settings.primary_color || ""} onChange={(e) => patch({ primary_color: e.target.value })} className="font-mono" />
                  </div>
                </div>
                <div>
                  <Label>Version label</Label>
                  <Input value={settings.version_label || ""} onChange={(e) => patch({ version_label: e.target.value })} placeholder="v1.0" className="mt-1" />
                </div>
              </div>
            </CollapsibleSection>
          )}
        </div>
      )}

      {/* ---------- ENGINEERING ---------- */}
      {tab === "engineering" && (
        <div className="space-y-4">
          {isAdmin && (
            <CollapsibleSection id="apps-manager" title="Apps" description="Register and manage the app tiles shown on the Dashboard." icon={LayoutGrid}>
              <AppsManager embedded />
            </CollapsibleSection>
          )}

          {isAdmin && (
            <CollapsibleSection id="dashboard-sections" title="Dashboard sections" description="Toggle which panels appear on the main dashboard." icon={LayoutGrid}>
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <div className="font-medium">Show Quick Launch panel</div>
                    <div className="text-[12.5px] text-muted-foreground">3-wide app grid on the right of the featured article.</div>
                  </div>
                  <Switch
                    data-testid="settings-quick-launch-switch"
                    checked={settings.show_quick_launch !== false}
                    onCheckedChange={(v) => patchAndSave({ show_quick_launch: v })}
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <div className="font-medium">Show Featured Article</div>
                    <div className="text-[12.5px] text-muted-foreground">Hero card above the dashboard.</div>
                  </div>
                  <Switch
                    data-testid="settings-featured-switch"
                    checked={settings.show_featured_article !== false}
                    onCheckedChange={(v) => patchAndSave({ show_featured_article: v })}
                  />
                </div>
              </div>
            </CollapsibleSection>
          )}

          {isAdmin && (
            <CollapsibleSection
              id="email-safety"
              title="Outbound Email Safety"
              description="Pause outbound email or restrict it to QA-allowlisted recipients."
              icon={settings.email_paused ? MailX : MailCheck}
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <div className="font-medium">Pause outbound email</div>
                    <div className="text-[12.5px] text-muted-foreground">Safe for config and testing.</div>
                  </div>
                  <Switch
                    data-testid="settings-email-pause-switch"
                    checked={settings.email_paused}
                    onCheckedChange={(v) => patchAndSave({ email_paused: v })}
                  />
                </div>
                <div>
                  <Label className="text-[12.5px]">QA allowlist</Label>
                  <div className="mt-2 flex gap-2">
                    <Input placeholder="user@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAllowAndSave(); } }} />
                    <Button type="button" variant="outline" onClick={addAllowAndSave} className="gap-1"><Plus className="w-4 h-4" /> Add</Button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(settings.qa_allowlist || []).length === 0 && <div className="text-[12.5px] text-muted-foreground">No addresses yet.</div>}
                    {(settings.qa_allowlist || []).map((v) => (
                      <span key={v} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-[12px]">
                        {v}
                        <button onClick={() => removeAllowAndSave(v)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </CollapsibleSection>
          )}
        </div>
      )}

      {/* ---------- ADMIN ---------- */}
      {tab === "admin" && isAdmin && (
        <div className="space-y-4">
          <div className="rounded-lg border border-primary/30 bg-primary/5 text-primary p-3 text-[12.5px] inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> This section is only visible to Super Admins.
          </div>

          <CollapsibleSection id="users-manager" title="Users & scopes" description="Add, edit or masquerade as any user." icon={Users2}>
            <UsersManager embedded />
          </CollapsibleSection>

          <CollapsibleSection id="mfa-admin-settings" title="Multi-factor authentication" description="Require MFA org-wide, or force it for specific users from the row actions above." icon={ShieldCheck}>
            <MfaAdminSettings />
          </CollapsibleSection>

          <CollapsibleSection id="industry-alert-feeds" title="Industry Alert Feeds" description="Add, order and toggle the RSS/Atom feeds shown on the Industry Alerts page." icon={Radio}>
            <NewsFeedsEditor embedded />
          </CollapsibleSection>

          <CollapsibleSection
            id="watchlist-keywords"
            title="Watchlist keywords"
            description="Products, vendors & systems that light up Industry Alerts and the notifications bell."
            icon={Flame}
          >
            <WatchlistEditor settings={settings} patchAndSave={patchAndSave} />
          </CollapsibleSection>
        </div>
      )}
    </div>
  );
}
